// Funciones de utilidad general

import { Divider } from "primereact/divider";
import { CaseStatus, UserRole, PHONE_CODES } from "./constants";

/**
 * Devuelve un valor monetario formateado: 100000 -> 100.000,00
 * @param {number | string} value
 */
export function currencyFormat(value) {
  return Number(value).toLocaleString("es", {
    minimumFractionDigits: 2,
  });
}

/**
 * Devuelve el valor legible del sexo (Masculino o Femenino)
 * @param {"M" | "F"} sex
 */
export function readableSex(sex) {
  if (sex === "M") {
    return "Masculino";
  }
  return "Femenino";
}

/**
 * Devuelve la fecha en formato yyyy/mm/dd
 * @param {Date} date
 */
export function formatDate(date) {
  // Validate
  if (!(date instanceof Date)) {
    return date;
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
}

/**
 * Returns a date from yyyy-mm-dd to dd/mm/yyyy
 * @param {string} date
 */
export function formatDateString(date) {
  // Validate
  if (!date || typeof date !== "string") {
    return date;
  }

  const [year, month, day] = date.split("-");
  return `${day}/${month}/${year}`;
}

/**
 * Devuelve el nombre del banco a partir de su ID
 * @param {number | string} id
 * @param {Array | undefined} banks
 */
export function getBankNameFromId(id, banks) {
  const bank = banks?.find((bank) => Number(bank.id) === Number(id));
  return bank?.name ?? "";
}

/**
 * Devuelve una cédula formateada: V12345678 -> V-12345678
 * @param {string} ci
 */
export function formatCi(ci) {
  // Validate
  if (!ci || typeof ci !== "string") {
    return ci;
  }

  return ci.replace(/^([VE])-?/, "$1-");
}

/**
 * Returns phone number formatted: 584243001234 -> 0424-3001234
 * @param {string} phoneNumber
 */
export function formatFullNumber(phoneNumber) {
  // Validate
  if (!phoneNumber || typeof phoneNumber !== "string") {
    return phoneNumber;
  }

  return phoneNumber.replace(/^58/, "0").replace(/(\d{4})(\d{7})/, "$1-$2");
}

/**
 * Returns HTTP header part for authorization
 */
export function getAuthHeader() {
  const token = localStorage.getItem("token");
  return {
    Authorization: `Token ${token}`,
  };
}

/**
 * Helper function to handle fetch requests
 * @param {string} url
 * @param {object} options
 * @returns {Promise<any>}
 */
export async function fetchData(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeader(),
      ...options.headers,
    },
  });
  if (!response.ok) {
    throw new Error(JSON.stringify(await response.json()));
  }
  return response.json();
}

/**
 * Get available options to change medical case status for current user role
 */
export function getStatusChangeOptionsForRole(currentStatus) {
  const role = localStorage.getItem("role");

  if (role === UserRole.MEDICAL_CENTER) {
    if (currentStatus === CaseStatus.PENDIENTE)
      return [CaseStatus.PENDIENTE, CaseStatus.ATENDIDO];
    if (currentStatus === CaseStatus.ATENDIDO)
      return [CaseStatus.ATENDIDO, CaseStatus.REMESADO];
    return [CaseStatus.ATENDIDO, CaseStatus.REMESADO, CaseStatus.PENDIENTE];
  }
  if (role === UserRole.MASTER) {
    return Object.values(CaseStatus);
  }

  return [];
}

/**
 * Returns the difference in days between a date and the current
 * @param {Date} date
 */
export function dateDiffFromNow(date) {
  const currentDate = new Date();

  // Calculate the difference in time (in milliseconds)
  const timeDifference = currentDate - date;

  // Convert the difference from milliseconds to days
  return Math.floor(timeDifference / (1000 * 60 * 60 * 24));
}

export function truncateText(text, maxLength) {
  if (!text) return "";
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + "...";
  }
  return text;
}

/**
 * Handle phone number paste event to automatically fill country code and number fields
 * @param {ClipboardEvent} e
 * @param {string} codeField
 * @param {string} numberField
 * @param {object} form
 */
export function handlePhonePaste(e) {
  const text = e.clipboardData.getData("text/plain");
  const newText = text.replace(/\D/g, "");
  let code = "";
  let phoneNumber = "";

  if (newText.length === 11) {
    code = newText.slice(0, 4);
    phoneNumber = newText.slice(4);
  } else if (newText.length === 10) {
    code = "0" + newText.slice(0, 3);
    phoneNumber = newText.slice(3);
  } else if (newText.length === 12) {
    code = "0" + newText.slice(2, 5);
    phoneNumber = newText.slice(5);
  }

  e.preventDefault();
  // using findPhoneCode
  const phoneCode = findPhoneCode(code);
  if (phoneCode) {
    code = phoneCode.value;
  } else {
    code = "0412"; // Default code if not found
  }

  return { code, phoneNumber };
}

/**
 * Find phone code by value or label
 * @param {string} phoneCode
 */
export function findPhoneCode(phoneCode) {
  return PHONE_CODES.find((item) => {
    return item.value === phoneCode || item.label === phoneCode;
  });
}

/**
 * Get formatted chat timestamp
 * @param {string} timestamp
 */
export function getChatTimestamp(timestamp) {
  if (!timestamp) return "";

  const date = new Date(timestamp);

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (msgDate.getTime() === today.getTime()) {
    // Show time: hh:mm
    return date.toLocaleTimeString("es-VE", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } else if (msgDate.getTime() === yesterday.getTime()) {
    return "Ayer";
  } else {
    // dd/mm/yy
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  }
}

/**
 * Person search item template
 * @param {(item: import("./MedicalCases/CreateCase").API_Person) => void} onClick
 */
export function createSearchItemTemplate(onClick) {
  /** @param {import("./MedicalCases/CreateCase").API_Person} item */
  return (item) => (
    <>
      <span className="py-1" onClick={() => onClick(item)}>
        {item.ci} - {item.name}
      </span>
      <Divider />
    </>
  );
}
